import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { trials } from '../utils/api';
import { ArrowLeft, MessageSquare, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const formatChanges = (changes = {}) => {
    const rows = [];
    if (changes.date) rows.push(['Date', changes.date]);
    if (changes.time) rows.push(['Time', changes.time]);
    if (changes.location) rows.push(['Location', changes.location]);
    return rows;
};

const Messages = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actioning, setActioning] = useState(null); // `${bookingId}:${requestId}:${action}`
    const [counterFor, setCounterFor] = useState(null); // requestId being countered
    const [counterDraft, setCounterDraft] = useState({ date: '', time: '', location: '' });

    const isEmployer = user?.user_type === 'company';

    const loadData = async () => {
        try {
            const res = await trials.getMyBookings();
            setBookings(res.data.bookings || []);
        } catch (err) {
            toast.error(err.userMessage || 'Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); /* eslint-disable-next-line */ }, []);

    // Build a flat list of pending edit requests addressed to this user.
    const inbox = bookings.flatMap(b =>
        (b.edit_requests || [])
            .filter(r => r.status === 'pending')
            .filter(r => isEmployer ? r.initiated_by === 'candidate' : r.initiated_by === 'employer')
            .map(r => ({ booking: b, request: r }))
    );

    const handleAction = async (booking, request, action, changes) => {
        const key = `${booking.id}:${request.id}:${action}`;
        setActioning(key);
        try {
            await trials.respondEdit(booking.id, request.id, action, changes || {}, '');
            toast.success(
                action === 'accept'
                    ? 'Changes accepted — booking updated.'
                    : action === 'decline'
                        ? 'Request declined.'
                        : 'Counter-edit sent.'
            );
            setCounterFor(null);
            setCounterDraft({ date: '', time: '', location: '' });
            await loadData();
        } catch (err) {
            toast.error(err.userMessage || 'Failed to respond');
        } finally {
            setActioning(null);
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="sticky top-0 z-50 bg-[#F0FDF4]/80 backdrop-blur-xl border-b border-purple-200/50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                    </div>
                    <Button variant="ghost" className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 md:px-8 py-10">
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Communication
                </span>
                <h1 className="text-4xl font-bold mb-8 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Messages</h1>

                {loading ? (
                    <div className="bg-white rounded-3xl p-12 text-center border border-purple-200/50 shadow-sm">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#8B5CF6] border-t-transparent mx-auto" />
                    </div>
                ) : inbox.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 text-center border border-purple-200/50 shadow-sm" data-testid="messages-empty">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3E8FF' }}>
                            <MessageSquare className="h-8 w-8" style={{ color: '#9333EA' }} />
                        </div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>No pending edit requests</h3>
                        <p className="text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>You'll see edit requests for your bookings here.</p>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="messages-inbox">
                        {inbox.map(({ booking, request }) => {
                            const counterpartyName = isEmployer
                                ? (booking.candidate_name || 'Candidate')
                                : (booking.company_name || 'Employer');
                            const isCountering = counterFor === request.id;
                            return (
                                <div key={request.id} className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm" data-testid={`edit-request-card-${request.id}`}>
                                    <div className="flex items-start justify-between gap-4 mb-3">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Pencil className="h-4 w-4 text-[#7C3AED]" />
                                                <span className="text-xs uppercase tracking-[0.2em] text-[#8B5CF6]" style={{ fontFamily: 'Unbounded, sans-serif' }}>Edit request</span>
                                            </div>
                                            <h4 className="font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                                {counterpartyName}
                                            </h4>
                                            <p className="text-sm text-slate-500" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                                {booking.job_title} &middot; Currently starts {booking.start_date}{booking.start_time ? ` at ${booking.start_time} (UK time)` : ''}{booking.location ? ` · ${booking.location}` : ''}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-purple-200/50 bg-purple-50/40 p-4 mb-3 text-sm" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                        <p className="text-xs uppercase tracking-[0.2em] text-[#8B5CF6] mb-2">Proposed changes</p>
                                        <div className="space-y-1">
                                            {formatChanges(request.changes).map(([k, v]) => (
                                                <div key={k} className="flex justify-between gap-3">
                                                    <span className="text-slate-500">{k}</span>
                                                    <span className="text-[#0A0A0A] font-semibold">{v}</span>
                                                </div>
                                            ))}
                                            {formatChanges(request.changes).length === 0 && (
                                                <span className="text-slate-500">No fields specified.</span>
                                            )}
                                        </div>
                                        {request.message && (
                                            <p className="mt-3 text-xs text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                                "{request.message}"
                                            </p>
                                        )}
                                    </div>

                                    {!isCountering ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Button
                                                className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full text-sm"
                                                disabled={!!actioning}
                                                onClick={() => handleAction(booking, request, 'accept')}
                                                data-testid={`accept-edit-btn-${request.id}`}
                                            >
                                                <Check className="h-4 w-4 mr-1" /> Accept
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="rounded-full border-purple-200 text-slate-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-sm"
                                                disabled={!!actioning}
                                                onClick={() => handleAction(booking, request, 'decline')}
                                                data-testid={`decline-edit-btn-${request.id}`}
                                            >
                                                <X className="h-4 w-4 mr-1" /> Decline
                                            </Button>
                                            {isEmployer && (
                                                <Button
                                                    variant="outline"
                                                    className="rounded-full border-purple-200 text-[#7C3AED] hover:bg-purple-50 text-sm"
                                                    disabled={!!actioning}
                                                    onClick={() => { setCounterFor(request.id); setCounterDraft({ date: '', time: '', location: '' }); }}
                                                    data-testid={`counter-edit-btn-${request.id}`}
                                                >
                                                    <Pencil className="h-4 w-4 mr-1" /> Counter-edit
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-purple-200 p-4 space-y-3" data-testid={`counter-form-${request.id}`}>
                                            <p className="text-xs text-slate-500" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                                Propose your own changes. Leave a field blank to keep current.
                                            </p>
                                            <input type="date" value={counterDraft.date} onChange={(e) => setCounterDraft(d => ({ ...d, date: e.target.value }))}
                                                className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                                                data-testid={`counter-date-input-${request.id}`} />
                                            <input type="text" placeholder="New time (e.g. 10:00 AM)" value={counterDraft.time} onChange={(e) => setCounterDraft(d => ({ ...d, time: e.target.value }))}
                                                className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                                                data-testid={`counter-time-input-${request.id}`} />
                                            <input type="text" placeholder="New location" value={counterDraft.location} onChange={(e) => setCounterDraft(d => ({ ...d, location: e.target.value }))}
                                                className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                                                data-testid={`counter-location-input-${request.id}`} />
                                            <div className="flex justify-end gap-2 pt-1">
                                                <Button variant="outline" className="rounded-full" onClick={() => setCounterFor(null)} disabled={!!actioning}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    className="rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90"
                                                    disabled={!!actioning || !(counterDraft.date || counterDraft.time || counterDraft.location)}
                                                    onClick={() => {
                                                        const changes = {};
                                                        if (counterDraft.date.trim()) changes.date = counterDraft.date.trim();
                                                        if (counterDraft.time.trim()) changes.time = counterDraft.time.trim();
                                                        if (counterDraft.location.trim()) changes.location = counterDraft.location.trim();
                                                        handleAction(booking, request, 'counter', changes);
                                                    }}
                                                    data-testid={`counter-send-btn-${request.id}`}
                                                >
                                                    Send counter
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Messages;
